import { Box, paletteColors, Theme, Typography, useMediaQuery } from '@evaneos/b2b-ui';
import { alpha } from '@evaneos/b2b-ui';

import { useIntl } from 'react-intl';
import { differenceInHours } from 'date-fns';

import { deprecated_BEDCC8 } from '../../styles/deprecated-colors';

import { getAgentDetailsFromEtsToken } from '../../resources/login/operations';
import { useTrackPageViewAmplitudeOnce } from '../../shared/analytics';
import {
    useGetNewRequestDiscussions,
    useGetResponseRequiredDiscussions,
} from '../../resources/inbox/hooks';
import { parseQueryString } from '../../shared/utilities/queryString';
import { getEntryPoint } from '../../shared/analytics/sessionStorage-utilities';
import { HomepageViewedProperties } from '../../shared/analytics/__AUTO_GENERATED__';
import { GlobalLoader } from '../../shared/components/layout/GlobalLoader';
import { Loader } from '../../design-system/Loader';
import {
    useFilteredNotifications,
    useRealTimeNotifications,
} from '../../resources/notifications/hooks';

import { AB_FlexOfferHomepageCard } from '../../pages/AB_FlexOfferSuivi';

import { AppointmentConnectionBanner } from './AppointmentConnectionBanner';

import { NewRequestsBlock } from './NewRequests/NewRequestBlock';
import { MessageListBlock } from './MessageList/MessageListBlock';
import { NotificationsBlock } from './Notifications/NotificationsBlock';
import { NotificationsDrawer } from './Notifications/NotificationsDrawer';
/* AB ab_flex_offer — sunsets 2026-10-31, delete this import + its usage below */

export const Homepage = () => {
    const { formatMessage } = useIntl();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    const isLargeScreenSize = useMediaQuery((theme: Theme) => theme.breakpoints.up('xxl'));
    const agentDetails = getAgentDetailsFromEtsToken();
    const firstName = agentDetails?.userFirstName || '';

    const query = parseQueryString(location.search);
    const {
        data: responseRequiredDossiers,
        isSuccess: isResponseRequiredSuccess,
        isLoading: isResponseRequiredLoading,
    } = useGetResponseRequiredDiscussions(query);
    const {
        data: newRequests,
        isSuccess: isNewRequestsSuccess,
        isLoading: isNewRequestsLoading,
    } = useGetNewRequestDiscussions(query);

    const {
        data: notifications,
        status: notificationsStatus,
        isLoading: areNotificationsLoading,
    } = useFilteredNotifications();

    useRealTimeNotifications();

    const isLoading = isResponseRequiredLoading || isNewRequestsLoading;

    useTrackPageViewAmplitudeOnce(
        (ampli, defaultProperties) => {
            const entryPoint = getEntryPoint<HomepageViewedProperties['entry_point']>();

            // Calculate overdue messages (response required > 48h)
            const nb_overdue_messages = responseRequiredDossiers.filter((dossier) => {
                const { lastTravellerToAgentMessageDate } = dossier.discussion;
                if (!lastTravellerToAgentMessageDate) return false;
                const hoursSinceLastTravellerMessage = differenceInHours(
                    new Date(),
                    lastTravellerToAgentMessageDate,
                );
                return hoursSinceLastTravellerMessage > 48;
            }).length;

            // Calculate overdue new requests (new requests >= 24h)
            const nb_overdue_new_files = newRequests.filter((dossier) => {
                if (!dossier.requestDate) return false;
                const hoursSinceRequest = differenceInHours(new Date(), dossier.requestDate);
                return hoursSinceRequest >= 24;
            }).length;

            ampli.homepageViewed({
                ...defaultProperties,
                entry_point: entryPoint ?? 'default_redirect',
                nb_messages: responseRequiredDossiers.length,
                nb_new_files: newRequests.length,
                nb_overdue_messages: nb_overdue_messages.toString(),
                nb_overdue_new_files: nb_overdue_new_files.toString(),
                nb_notification: notifications.length,
            });
        },
        {
            dependencies: [responseRequiredDossiers, newRequests],
            isReady: isResponseRequiredSuccess && isNewRequestsSuccess,
        },
    );

    return (
        <Box
            data-spec-id="homepage"
            flex={1}
            sx={{
                backgroundColor: 'var(--deprecated_f9f8f3)',
                backgroundImage: `
                    radial-gradient(
                      circle at 80% 10%,
                      ${alpha(deprecated_BEDCC8, 0.4)},
                      transparent 60%
                    )
                `,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                overflowY: 'auto',
            }}
        >
            <AppointmentConnectionBanner />
            {isLoading ? (
                <Box
                    display="flex"
                    height="100%"
                    alignItems="center"
                    justifyContent="center"
                    flexDirection="column"
                    gap={2}
                    data-testid="homepage-loading"
                >
                    <Loader color={paletteColors.green[700]} />
                    <Typography variant="h3" component="p">
                        <GlobalLoader />
                    </Typography>
                </Box>
            ) : (
                <Box
                    padding={{ xs: '40px 16px', md: '40px 24px' }}
                    maxWidth={{ xs: '1496px', xxl: '1920px' }}
                    margin="0 auto"
                    minHeight="100%"
                    display="flex"
                    flexDirection="column"
                    data-testid="homepage-content"
                >
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={2}
                    >
                        <Typography variant="titleMoretXL" component="h1">
                            {formatMessage(
                                { id: 'homepage.title' },
                                {
                                    name: firstName,
                                },
                            )}
                        </Typography>

                        {!isLargeScreenSize ? (
                            <NotificationsDrawer
                                notifications={notifications}
                                notificationsStatus={notificationsStatus}
                                areNotificationsLoading={areNotificationsLoading}
                            />
                        ) : null}
                    </Box>
                    {/* BEGIN - AB ab_flex_offer — sunsets 2026-10-31 */}
                    <AB_FlexOfferHomepageCard />
                    {/* END - AB ab_flex_offer */}
                    <Box
                        sx={{
                            flex: 1,
                            display: 'grid',
                            gap: 3,
                            gridTemplateColumns: {
                                xs: 'minmax(0, 1fr)',
                                md: 'minmax(300px, 2fr) minmax(300px, 1fr)',
                                xxl: 'minmax(400px, 2fr) minmax(400px, 1fr) minmax(400px, 1fr)',
                            },
                            gridAutoRows: {
                                xs: 'minmax(200px, auto)',
                                md: 'minmax(350px, 1fr)',
                            },
                        }}
                    >
                        {isMobile ? (
                            <>
                                <NewRequestsBlock newRequests={newRequests} />
                                <MessageListBlock
                                    responseRequiredDossiers={responseRequiredDossiers}
                                />
                            </>
                        ) : (
                            <>
                                <MessageListBlock
                                    responseRequiredDossiers={responseRequiredDossiers}
                                />
                                <NewRequestsBlock newRequests={newRequests} />
                            </>
                        )}
                        {isLargeScreenSize ? (
                            <NotificationsBlock
                                notifications={notifications}
                                notificationsStatus={notificationsStatus}
                                areNotificationsLoading={areNotificationsLoading}
                            />
                        ) : null}
                    </Box>
                </Box>
            )}
        </Box>
    );
};
